<template>
    <div class="cal-mobile">
        <div class="cal-mobile__grid">
            <div class="cal-mobile__weekdays" aria-hidden="true">
                <div v-for="label in WEEKDAY_LABELS" :key="label" class="cal-mobile__weekday">
                    {{ label }}
                </div>
            </div>
            <div class="cal-mobile__days" role="grid" aria-label="Календарь матчей">
                <div
                    v-for="(week, weekIndex) in weeks"
                    :key="weekIndex"
                    class="cal-mobile__week"
                    role="row"
                >
                    <div
                        v-for="(cell, dayIndex) in week"
                        :key="cell.dateKey || `pad-${weekIndex}-${dayIndex}`"
                        class="cal-mobile__cell"
                        :class="{ 'cal-mobile__cell--pad': !cell.dateKey }"
                        role="gridcell"
                        :aria-selected="cell.dateKey ? cell.dateKey === selectedDateKey : undefined"
                    >
                        <button
                            v-if="cell.dateKey"
                            :ref="(el) => setDayRef(cell.dateKey, el)"
                            type="button"
                            class="cal-mobile__day"
                            :class="{
                                'is-today': cell.isToday,
                                'is-selected': cell.dateKey === selectedDateKey,
                            }"
                            :tabindex="tabIndexFor(cell)"
                            :aria-current="cell.isToday ? 'date' : undefined"
                            :aria-label="formatDayAriaLabel(cell.dateKey, cell.games.length)"
                            @click="selectedDateKey = cell.dateKey"
                            @keydown="onDayKeydown($event, weekIndex * 7 + dayIndex)"
                        >
                            <span class="cal-mobile__num">{{ cell.day }}</span>
                            <span v-if="cell.games.length" class="cal-mobile__dot" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="cal-mobile__panel">
            <div aria-live="polite">
                <h3 v-if="selectedCell" class="cal-mobile__heading">
                    {{ dateLabel }}
                    <span v-if="matchCountLabel" class="cal-mobile__count"> · {{ matchCountLabel }}</span>
                </h3>
                <p v-if="!selectedCell" class="cal-mobile__empty">В этом месяце матчей нет</p>
                <p v-else-if="!selectedCell.games.length" class="cal-mobile__empty">В этот день матчей нет</p>
            </div>
            <div v-if="selectedCell?.games.length" class="cal-mobile__matches">
                <CalendarMatchItem
                    v-for="game in selectedCell.games"
                    :key="game.id"
                    :game="game"
                    variant="panel"
                    @select="$emit('select-game', $event)"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import CalendarMatchItem from './CalendarMatchItem.vue';
import {
    WEEKDAY_LABELS,
    buildCalendarCells,
    formatDayAriaLabel,
    formatDateKey,
    formatDayHeading,
    formatMatchCount,
    pickDefaultDateKey,
} from '@/utils/calendarMonth';

const props = defineProps({
    year: { type: Number, required: true },
    month: { type: Number, required: true },
    gamesByDate: { type: Object, default: () => ({}) },
    todayRequest: { type: Number, default: 0 },
});

const emit = defineEmits(['select-game', 'select-date']);

const selectedDateKey = ref(null);
const dayButtons = new Map();

const cells = computed(() => buildCalendarCells(props.year, props.month, props.gamesByDate));

const weeks = computed(() => {
    const rows = [];
    for (let index = 0; index < cells.value.length; index += 7) {
        rows.push(cells.value.slice(index, index + 7));
    }
    return rows;
});

const selectedCell = computed(() => (
    cells.value.find((cell) => cell.dateKey && cell.dateKey === selectedDateKey.value) || null
));

const dateLabel = computed(() => (
    selectedCell.value ? formatDayHeading(selectedCell.value.dateKey) : ''
));

const matchCountLabel = computed(() => {
    const count = selectedCell.value?.games.length || 0;
    return count ? formatMatchCount(count) : '';
});

watch(
    () => `${props.year}-${props.month}`,
    () => {
        selectedDateKey.value = pickDefaultDateKey(cells.value);
    },
    { immediate: true },
);

watch(selectedDateKey, (key) => {
    emit('select-date', key);
}, { immediate: true });

watch(
    () => props.todayRequest,
    (request) => {
        if (!request) {
            return;
        }

        const key = formatDateKey(new Date());
        if (cells.value.some((cell) => cell.dateKey === key)) {
            selectedDateKey.value = key;
        }
    },
);

function setDayRef(dateKey, el) {
    if (!dateKey) {
        return;
    }
    if (el) {
        dayButtons.set(dateKey, el);
    } else {
        dayButtons.delete(dateKey);
    }
}

function tabIndexFor(cell) {
    const current = selectedDateKey.value
        || cells.value.find((item) => item.dateKey)?.dateKey;
    return cell.dateKey === current ? 0 : -1;
}

function onDayKeydown(event, index) {
    const deltaByKey = {
        ArrowLeft: -1,
        ArrowRight: 1,
        ArrowUp: -7,
        ArrowDown: 7,
    };
    const delta = deltaByKey[event.key];
    if (delta == null) {
        return;
    }

    event.preventDefault();
    const next = cells.value[index + delta];
    if (!next?.dateKey) {
        return;
    }

    selectedDateKey.value = next.dateKey;
    nextTick(() => {
        dayButtons.get(next.dateKey)?.focus();
    });
}
</script>

<style lang="scss" scoped>
@import '../../../../scss/tokens';

.cal-mobile__grid {
    background: rgba(255, 255, 255, 0.55);
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: $radius-md;
    overflow: hidden;
}

.cal-mobile__weekdays,
.cal-mobile__week {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
}

.cal-mobile__weekday {
    padding: 0.55rem 0.15rem;
    text-align: center;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: $color-brand;
    background: $color-brand-alpha-soft;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    border-right: 1px solid rgba(15, 23, 42, 0.08);

    &:nth-child(7) {
        border-right: 0;
    }
}

.cal-mobile__cell {
    display: flex;
    min-height: $touch-min;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    border-right: 1px solid rgba(15, 23, 42, 0.08);

    &--pad {
        background: rgba(232, 238, 245, 0.55);
    }

    &:nth-child(7) {
        border-right: 0;
    }
}

.cal-mobile__week:last-child .cal-mobile__cell {
    border-bottom: 0;
}

.cal-mobile__day {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    flex: 1;
    width: 100%;
    min-height: $touch-min;
    margin: 0;
    padding: 0.2rem;
    border: 0;
    background: rgba(255, 255, 255, 0.72);
    color: $color-text-primary;
    font: inherit;
    cursor: pointer;
    touch-action: manipulation;

    &:hover:not(.is-selected) {
        background: $color-brand-alpha-soft;
    }

    &:focus-visible {
        outline: 2px solid $color-brand;
        outline-offset: -2px;
        z-index: 1;
    }

    &.is-today:not(.is-selected) {
        box-shadow: inset 0 0 0 2px $color-brand;

        .cal-mobile__num {
            color: $color-brand;
        }
    }

    &.is-selected {
        background: $color-brand;
        color: #fff;

        .cal-mobile__dot {
            background: #fff;
        }
    }

    &.is-today.is-selected {
        box-shadow: inset 0 0 0 2px #fff;
    }

    &.is-selected:focus-visible {
        outline-color: #fff;
    }
}

.cal-mobile__num {
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1;
}

.cal-mobile__dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: $color-brand;
}

.cal-mobile__panel {
    margin-top: 1rem;
}

.cal-mobile__heading {
    margin: 0 0 0.75rem;
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.3;
    color: $color-text-primary;
}

.cal-mobile__count {
    font-weight: 600;
    color: $color-text-secondary;
}

.cal-mobile__empty {
    margin: 0;
    font-size: 0.95rem;
    color: $color-text-secondary;
}

.cal-mobile__matches {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}
</style>
